/**
 * Page-independent upload manager — the commitment model's engine.
 *
 * Media commits the moment upload completes: each file is preprocessed in the
 * browser, PUT as an asset, and immediately turned into an `image` entry. From
 * that instant the media exists in the library, whatever happens to the page
 * being edited. Placement (appending the entry id to a block) is the caller's
 * concern, via the per-file `on_entry` callback.
 *
 * Jobs live in this module, not in a component: navigation or unmount never
 * aborts or orphans work in progress. Nothing transient (blob previews,
 * progress) ever enters a document model.
 */
import {
  preprocess_media,
  upload_with_progress,
  prettify_file_name,
  fetch_json,
  fetch_entries,
} from '#lib/util.js';

const MAX_CONCURRENT_UPLOADS = 2;

let job_counter = 0;
let active_count = 0;
const queue = [];
const idle_waiters = [];

export const upload_state = $state({
  /** @type {Array<object>} */
  jobs: [],
});

export function get_jobs() {
  return upload_state.jobs;
}

export function is_busy() {
  return upload_state.jobs.some(job => ['queued', 'uploading', 'creating'].includes(job.status));
}

export function busy_count() {
  return upload_state.jobs.filter(job => ['queued', 'uploading', 'creating'].includes(job.status)).length;
}

export function optimizing_count() {
  return upload_state.jobs.filter(job => job.status === 'optimizing').length;
}

export function failed_count() {
  return upload_state.jobs.filter(job => job.status === 'failed').length;
}

/** Resolves once no job is queued/uploading/creating (optimizing does NOT block). */
export function wait_for_idle() {
  if (!is_busy()) return Promise.resolve();
  return new Promise(resolve => idle_waiters.push(resolve));
}

function notify_if_idle() {
  if (!is_busy()) {
    while (idle_waiters.length) idle_waiters.shift()();
  }
}

/**
 * Add files as media entries.
 *
 * @param {File[]|FileList} files
 * @param {object} [options]
 * @param {string} [options.target] Opaque key (e.g. a block_id) used by UIs to render
 *   the job's placeholder where the drop happened.
 * @param {(entry: object, job: object) => void} [options.on_entry] Called per file as soon as
 *   its media entry exists (this is where placement happens).
 * @param {(job: object) => void} [options.on_error] Called per file on failure.
 * @returns {Promise<object[]>} Resolves with the created entries once every file settled
 *   (failed files are simply absent from the result).
 */
export function add_media(files, { target = null, on_entry, on_error } = {}) {
  const file_list = Array.from(files || []);
  if (file_list.length === 0) return Promise.resolve([]);

  const jobs = file_list.map(file => {
    const job = $state({
      id: `job-${++job_counter}`,
      file_name: file.name,
      status: 'queued', // queued | uploading | creating | optimizing | ready | failed
      progress: 0,
      error: null,
      target,
      entry_id: null,
      entry: null,
      preview_url: null,
      // kept for retry — the browser-side work is not repeated
      _file: file,
      _optimized_file: null,
      _metadata: null,
      _analysis: null,
      _on_entry: on_entry,
      _on_error: on_error,
    });
    upload_state.jobs.push(job);
    return job;
  });

  const settled = Promise.all(jobs.map(job => enqueue(job).catch(() => null)));
  return settled.then(entries => entries.filter(Boolean));
}

function enqueue(job) {
  return new Promise((resolve, reject) => {
    queue.push({ job, resolve, reject });
    pump();
  });
}

function pump() {
  while (active_count < MAX_CONCURRENT_UPLOADS && queue.length > 0) {
    const { job, resolve, reject } = queue.shift();
    active_count++;
    run_job(job)
      .then(resolve, reject)
      .finally(() => {
        active_count--;
        pump();
        notify_if_idle();
      });
  }
}

async function run_job(job) {
  try {
    // 1. Browser-side preprocess (resize to master, HEIC→WebP, pass-through for
    //    already-optimized formats). Skipped on retry — the result is kept.
    if (!job._optimized_file) {
      const result = await preprocess_media(job._file);
      if (result?.errors?.length) {
        throw Object.assign(new Error('preprocess_failed'), { codes: result.errors });
      }
      if (!(result?.optimized_file && result?.metadata)) {
        throw new Error('preprocess_failed');
      }
      job._optimized_file = result.optimized_file;
      job._metadata = result.metadata;
    }

    if (job.preview_url) URL.revokeObjectURL(job.preview_url);
    job.preview_url = URL.createObjectURL(job._optimized_file);

    // 2. Upload — media commits when this completes
    job.status = 'uploading';
    job.progress = 0;
    const upload_result = await upload_with_progress(job._optimized_file, job._metadata, p => {
      job.progress = p;
    });
    const asset_id = upload_result.path;
    job._analysis = upload_result.analysis ?? null;
    job._existing_entry_id = upload_result.existing_entry_id ?? null;

    // 3. Create the media entry immediately — the library is the safety net
    job.status = 'creating';
    const { media_type, width, height, alt, duration, audio_track } = job._metadata;
    const entry_json = {
      type: 'image',
      visibility: 'public',
      media_type,
      src: `/assets/${asset_id}`,
      title: prettify_file_name(job.file_name.split('.')[0]),
      alt: alt || '',
      description: '',
      preamble: '',
      width,
      height,
      focal_point_x: job._analysis?.focal_point_x ?? 0.5,
      focal_point_y: job._analysis?.focal_point_y ?? 0.5,
      focal_point_auto: job._analysis ? true : undefined,
      fit_to_frame: job._analysis?.graphic ? true : false,
      fit_auto: job._analysis ? true : undefined,
      tag_keys: [],
      ...(media_type === 'video' ? { duration, audio_track } : {}),
    };

    let created;
    if (job._existing_entry_id) {
      // Dedupe: the identical file already has a media entry — reuse it.
      created = { entry_id: job._existing_entry_id, deduped: true };
    } else {
      created = await fetch_json('POST', '/api/create-entry', entry_json);
    }

    job.entry_id = created.entry_id;
    job.deduped = Boolean(created.deduped);

    // Resolve a proper preview shape for pickers/placement UIs
    try {
      const previews = await fetch_entries([created.entry_id], { as_preview: true });
      job.entry = previews?.[0] || { ...entry_json, entry_id: created.entry_id };
    } catch {
      job.entry = { ...entry_json, entry_id: created.entry_id };
    }

    // 4. Placement callback — the only part owned by the calling surface
    job.status = media_type === 'image' ? 'optimizing' : 'ready';
    try {
      job._on_entry?.(job.entry, job);
    } catch (err) {
      console.error('add_media on_entry callback failed:', err);
    }

    if (job.status === 'optimizing') {
      watch_optimization(job, asset_id);
    }

    return job.entry;
  } catch (err) {
    console.error('Media upload failed:', err);
    job.status = 'failed';
    job.error = err;
    try {
      job._on_error?.(job);
    } catch { /* listener errors must not mask the failure */ }
    throw err;
  }
}

/** Poll variant status without blocking anything — optimizing is informational. */
async function watch_optimization(job, asset_id) {
  const POLL_MS = 3000;
  const MAX_POLLS = 140;
  for (let i = 0; i < MAX_POLLS; i++) {
    await new Promise(r => setTimeout(r, POLL_MS));
    if (job.status !== 'optimizing') return; // removed or retried meanwhile
    try {
      const res = await fetch(`/api/image-variant-status?asset_id=${encodeURIComponent(asset_id)}`);
      if (!res.ok) continue;
      const status = await res.json();
      if ((status?.pending ?? 0) <= 0) {
        job.status = 'ready';
        release_job_later(job);
        return;
      }
    } catch { /* transient — keep polling */ }
  }
  // Timeout: master image serves fine; the queue keeps retrying server-side.
  job.status = 'ready';
  release_job_later(job);
}

/** Retry a failed job (reuses the preprocessed file, fresh asset id). */
export function retry(job_id) {
  const job = upload_state.jobs.find(j => j.id === job_id);
  if (!job || job.status !== 'failed') return;
  job.status = 'queued';
  job.error = null;
  job.progress = 0;
  enqueue(job).catch(() => null);
}

/** Remove a job from the list (failed or done). Running jobs cannot be removed. */
export function remove(job_id) {
  const index = upload_state.jobs.findIndex(j => j.id === job_id);
  if (index === -1) return;
  const job = upload_state.jobs[index];
  if (['uploading', 'creating'].includes(job.status)) return;
  if (job.preview_url) URL.revokeObjectURL(job.preview_url);
  upload_state.jobs.splice(index, 1);
}

function release_job_later(job, delay = 4000) {
  // Keep 'ready' jobs around briefly so placement UIs can crossfade from the
  // placeholder to the real card, then clean up.
  setTimeout(() => {
    if (job.status === 'ready') remove(job.id);
  }, delay);
}

// Warn on tab close only while uploads are actually in flight — a warning that
// is sometimes meaningless is a warning nobody reads.
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', (event) => {
    if (is_busy()) {
      event.preventDefault();
      event.returnValue = '';
    }
  });
}
